/**
 * The tail's ROOT HANDLE (Allen, 2026-10-05, the lemur): "the tail offset starts a
 * bit too high and a bit too far behind". The measured root is the junction
 * ring's centre, and a zone grown up toward the belt lifts it off the body — the
 * eye knows where the tail should turn and the measurement does not. So the
 * pivot is a thing a person drags: a round handle on Tail1, shown while the
 * tail's flag is open (ask the question at the object), moving only in the
 * body's SIDE plane — up/down and forward/back, never sideways off the midline.
 *
 * The drag is a draft; the release commits ONE bind-world point (the Document's
 * `target.tails[].pivot`), replayed by the run. Bind ↔ world goes through the
 * hip (Tail1's parent), the hair skull's head product: mesh · bindMatrix⁻¹ ·
 * hip · boneInverse — so the handle can be dragged on a posed, held character.
 *
 * Depends on: three, the viewport's editing surface. Depended on by: main (the
 * tail flyout's open/close, the frame hook).
 */
import { Matrix4, Plane, Raycaster, Vector2, Vector3, type Object3D, type SkinnedMesh } from 'three';
import { isViewportDebris } from '@harryhausen/adapter-three';
import type { Viewport } from './viewport.js';

type V3 = { readonly x: number; readonly y: number; readonly z: number };

export interface TailRootHandleDeps {
  readonly viewport: Viewport;
  scene(): Object3D;
  /** Hold the mixer while a drag is in flight. */
  hold(on: boolean): void;
  /** A finished gesture: the pivot a person placed, bind-world. */
  place(pivot: V3): void;
}

export interface TailRootTarget {
  /** Tail1's scene bone (`Tail1_graft`). */
  readonly bone: string;
  /** The body's forward, bind-world, horizontal (the detector's frame) — the side plane is up × forward. */
  readonly forward: V3;
}

/** One arrow press, metres (Shift: ×5). */
export const TAIL_ROOT_NUDGE = 0.002;

function soleSkinned(root: Object3D): SkinnedMesh | null {
  let found: SkinnedMesh | null = null;
  let count = 0;
  root.traverse((o) => {
    if ((o as SkinnedMesh).isSkinnedMesh && !isViewportDebris(o)) {
      count++;
      found ??= o as SkinnedMesh;
    }
  });
  return count === 1 ? found : null;
}

export function createTailRootHandle(deps: TailRootHandleDeps) {
  const surface = deps.viewport.editingSurface();
  const style = document.createElement('style');
  style.textContent = `
    .tail-root-handle {position:fixed;z-index:1200;width:22px;height:22px;padding:0;border:3px solid #fff3d6dd;border-radius:50%;background:#f0b44266;box-shadow:0 0 0 4px #2a1c0840,0 2px 9px #0004;cursor:grab;touch-action:none;transform:translate(-50%,-50%)}
    .tail-root-handle:active {cursor:grabbing}
    .tail-root-handle:focus-visible {outline:3px solid #fff;outline-offset:6px}
    .tail-root-handle[hidden] {display:none}
  `;
  document.head.appendChild(style);
  const handle = document.createElement('button');
  handle.type = 'button';
  handle.className = 'tail-root-handle';
  handle.hidden = true;
  handle.dataset['testid'] = 'tail-root-handle';
  // Part of the tail flag's interaction: a click or fine drag here is not an outside click that closes it.
  handle.dataset['flyoutCompanion'] = '';
  handle.setAttribute('aria-label', 'Tail root: drag to move where the tail pivots (up/down, forward/back); arrows nudge, Shift+arrows nudge more');
  handle.title = 'Tail root — drag where the tail should pivot';
  document.body.appendChild(handle);

  let target: TailRootTarget | null = null;
  /** The pivot as drawn now, bind-world (a drag's draft); null = follow the bone. */
  let draft: Vector3 | null = null;
  const B = new Matrix4();

  /** Bind-world → world through the hip (Tail1's parent). */
  const hipTransform = (): boolean => {
    if (target === null) return false;
    const scene = deps.scene();
    const mesh = soleSkinned(scene);
    const tail1 = scene.getObjectByName(target.bone);
    const hip = tail1?.parent;
    if (mesh === null || hip === null || hip === undefined) return false;
    const i = mesh.skeleton.bones.findIndex((b) => b === hip);
    if (i < 0) return false;
    scene.updateMatrixWorld(true);
    B.copy(mesh.matrixWorld).multiply(mesh.bindMatrixInverse).multiply(hip.matrixWorld).multiply(mesh.skeleton.boneInverses[i]!);
    return true;
  };
  /** Tail1 now, bind-world (the declared pivot after a run, or the measured root). */
  const boneBind = (): Vector3 | null => {
    if (target === null || !hipTransform()) return null;
    const tail1 = deps.scene().getObjectByName(target.bone);
    if (tail1 === undefined) return null;
    return new Vector3().setFromMatrixPosition(tail1.matrixWorld).applyMatrix4(B.clone().invert());
  };
  const pivotBind = (): Vector3 | null => draft ?? boneBind();
  const pivotWorld = (): Vector3 | null => {
    const p = pivotBind();
    return p === null || !hipTransform() ? null : p.clone().applyMatrix4(B);
  };
  /** Up × forward: the body's left, bind-world — the side plane's normal. */
  const lateralBind = (): Vector3 => {
    const f = target!.forward;
    return new Vector3(0, 1, 0).cross(new Vector3(f.x, 0, f.z).normalize()).normalize();
  };

  /**
   * The handle hides for a moment while a placement re-mounts the character, and a hidden button
   * loses focus — the arrows then went nowhere after the first step lands. Focus held when it hid
   * comes back when it shows again, on the same tail.
   */
  let refocus = false;
  const frame = (): void => {
    const w = target === null ? null : pivotWorld();
    const at = w === null ? null : deps.viewport.projectPoint(w);
    if (at === null && !handle.hidden && document.activeElement === handle) refocus = true;
    handle.hidden = at === null;
    if (at !== null && refocus) {
      refocus = false;
      handle.focus({ preventScroll: true });
    }
    if (at !== null) {
      handle.style.left = `${at.x}px`;
      handle.style.top = `${at.y}px`;
    }
  };

  // ── the gestures ──
  /** `moved`: a pointermove landed — a press and release in place is a CLICK (focus for the arrows), never a placement. */
  let drag: { pointer: number; plane: Plane; moved: boolean; before: Vector3 | null } | null = null;
  const ray = new Raycaster();
  const hitOn = (event: PointerEvent, plane: Plane): Vector3 | null => {
    const rect = surface.canvas.getBoundingClientRect();
    ray.setFromCamera(new Vector2(((event.clientX - rect.left) / rect.width) * 2 - 1, (-(event.clientY - rect.top) / rect.height) * 2 + 1), surface.camera);
    return ray.ray.intersectPlane(plane, new Vector3());
  };
  const commit = (): void => {
    if (draft === null) return;
    deps.place({ x: draft.x, y: draft.y, z: draft.z });
  };
  handle.addEventListener('pointerdown', (event) => {
    if (event.button !== 0 || target === null) return;
    const w = pivotWorld();
    if (w === null) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    // The side plane through the pivot, carried to world through the hip.
    const normal = lateralBind().transformDirection(B);
    drag = { pointer: event.pointerId, plane: new Plane().setFromNormalAndCoplanarPoint(normal, w), moved: false, before: draft };
    draft = pivotBind();
    // preventDefault above also cancels the browser's focus-on-press, and the arrows need focus.
    handle.focus({ preventScroll: true });
    deps.hold(true);
    surface.navigation(false);
    handle.setPointerCapture(event.pointerId);
  });
  handle.addEventListener('pointermove', (event) => {
    if (drag === null || event.pointerId !== drag.pointer || !hipTransform()) return;
    const hit = hitOn(event, drag.plane);
    if (hit === null) return;
    draft = hit.applyMatrix4(B.clone().invert());
    drag.moved = true;
    frame();
  });
  /** End a drag in flight: capture, navigation and hold all let go. */
  const endDrag = (): void => {
    if (drag === null) return;
    if (handle.hasPointerCapture(drag.pointer)) handle.releasePointerCapture(drag.pointer);
    drag = null;
    surface.navigation(true);
    deps.hold(false);
  };
  const release = (event: PointerEvent): void => {
    if (drag === null || event.pointerId !== drag.pointer) return;
    const { moved, before } = drag;
    endDrag();
    if (moved) commit();
    else draft = before; // a click: nothing placed, the handle as it was
  };
  handle.addEventListener('pointerup', release);
  // An ABORTED gesture (a device interruption, lost capture) places nothing — the Esc rule (Codex P2, #253).
  handle.addEventListener('pointercancel', (event) => {
    if (drag === null || event.pointerId !== drag.pointer) return;
    endDrag();
    draft = null;
    frame();
  });
  /** Move the pivot in the side plane: `up` metres up, `fwd` metres toward the body's front. */
  const nudge = (up: number, fwd: number): void => {
    const p = pivotBind();
    if (p === null || target === null) return;
    const f = new Vector3(target.forward.x, 0, target.forward.z).normalize();
    draft = p.clone().add(new Vector3(0, up, 0)).add(f.multiplyScalar(fwd));
    frame();
    commit();
  };
  handle.addEventListener('keydown', (event) => {
    const step = TAIL_ROOT_NUDGE * (event.shiftKey ? 5 : 1);
    const moves: Record<string, [number, number]> = { ArrowUp: [step, 0], ArrowDown: [-step, 0], ArrowLeft: [0, -step], ArrowRight: [0, step] };
    const m = moves[event.key];
    if (m === undefined) return;
    event.preventDefault();
    nudge(...m);
  });

  return {
    /** Show the handle on this tail (its flag is open with a chain), or hide it (null). */
    show(next: TailRootTarget | null): void {
      // The flag closing mid-drag (Esc with the pointer down) CANCELS the drag — nothing is
      // placed, and the handle never strands on a closed tail (Codex P2, #253).
      if (drag !== null) {
        if (next !== null && next.bone === target?.bone) return; // the same tail re-synced: the drag keeps it
        endDrag();
      }
      // The SAME tail re-synced (a placement landed and the subject remounted) keeps the draft:
      // a queued placement's position is still what the person sees (Codex P2, #253).
      if (next === null || next.bone !== target?.bone) {
        draft = null;
        refocus = false; // another tail (or none): focus does not follow
      }
      target = next;
      frame();
    },
    /** The run re-placed the chain: follow the bone again. */
    settled(): void {
      if (drag === null) draft = null;
    },
    frame,
    probe(): { readonly on: boolean; readonly pivotBind: V3 | null; readonly handle: { x: number; y: number } | null } {
      const p = pivotBind();
      const w = pivotWorld();
      return {
        on: !handle.hidden,
        pivotBind: p === null ? null : { x: p.x, y: p.y, z: p.z },
        handle: handle.hidden || w === null ? null : deps.viewport.projectPoint(w),
      };
    },
    /** Test and smoke door: the gesture end a drag reaches, without a pointer. */
    nudge,
  };
}
export type TailRootHandle = ReturnType<typeof createTailRootHandle>;
